import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CommunityChoice } from "@/components/app/fields/community-choice";
import { PageHeader } from "@/components/app/shell/page-header";
import { SettingsScopeLine } from "@/components/app/rails/settings-scope-line";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { ShelbyRehearsal } from "./-components/shelby-rehearsal";

export const Route = createFileRoute("/_authenticated/_shelby/shelby/agent")({
  beforeLoad: ({ context }) => guardRoute(context.me, "shelby-agent"),
  component: ShelbyAgent,
});

/** The instruction the assistant would run with now, plus a test chat. The core instruction is code
 * (human first, no promises, a tour or call as the ask) and a community adds to it in Settings, so
 * improvements reach every community. */
function ShelbyAgent() {
  const { facilityId } = useFacility();
  const oneBuilding = facilityId === "all" ? null : facilityId;
  const rendered = useQuery({
    ...orpc.shelby.prompt.queryOptions({ input: { facilityId: oneBuilding ?? "" } }),
    enabled: Boolean(oneBuilding),
  });
  const agentName = rendered.data?.settings.agentName ?? "Shelby";

  if (!oneBuilding) {
    return (
      <>
        <SettingsScopeLine />
        <PageHeader title="Sales assistant agent" meta="What she says, and a place to try her." />
        <Card>
          <CardContent>
            <CommunityChoice prompt="Choose a building. Her instruction is built from that community's own lines, its roster and its knowledge base." />
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <SettingsScopeLine />
      <PageHeader
        title="Sales assistant agent"
        meta={`How ${agentName} sounds, what she knows, and a place to try her before a family does.`}
      />
      {rendered.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : rendered.isError ? (
        <Card className="py-0">
          <ReadFailed
            className="p-4"
            message={`Couldn't read ${agentName}'s instruction for this community.`}
            error={rendered.error}
            retrying={rendered.isFetching}
            onRetry={() => void rendered.refetch()}
          />
        </Card>
      ) : (
        <>
          <ShelbyRehearsal key={oneBuilding} facilityId={oneBuilding} agentName={agentName} />
          <Card>
            <CardHeader>
              <CardTitle>Her instruction, as it stands now</CardTitle>
              <CardDescription>
                Rendered from this community's settings, roster and knowledge base. Edit the parts
                that are yours in{" "}
                <Link
                  to="/shelby/settings"
                  className="text-primary focus-ring rounded-sm font-medium hover:underline"
                >
                  Sales assistant settings
                </Link>
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              <pre
                className="bg-sunken focus-ring max-h-[60vh] border overflow-auto rounded-md p-3 text-xs whitespace-pre-wrap"
                aria-label="Her instruction"
                // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrolling region must take focus to be scrolled without a pointer
                tabIndex={0}
              >
                {rendered.data?.prompt}
              </pre>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
